import { useEffect, useRef, useState } from 'react';

interface ScenarioItem {
  id: string;
  icon: string;
  title: string;
  description: string;
  iconBg?: string;
  color?: string;
}

interface WhenToUseProps {
  items?: ScenarioItem[];
  loading?: boolean;
}

export default function WhenToUse({ items = [], loading = false }: WhenToUseProps) {
  const [isVisible, setIsVisible] = useState(false);
  const scenarios = items; // Sử dụng items từ props
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} id="use-cases" className="py-24 bg-slate-50 relative overflow-hidden">
      {/* Background illustration */}
      {/* Background decoration */}
      <div className="absolute inset-0 opacity-15">
        <div className="absolute inset-0 animate-[drift_30s_ease-in-out_infinite]" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%2306b6d4' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }}></div>
      </div>

      <div className="max-w-[1280px] mx-auto px-6 relative z-10">
        {/* Header */}
        <div className={`text-center mb-16 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div className="inline-block px-4 py-2 bg-[#06b6d4]/10 rounded-full mb-6">
            <span className="text-[#06b6d4] text-sm font-semibold uppercase tracking-wider">Thời điểm phù hợp</span>
          </div>
          <h2 className="font-['Inter',sans-serif] text-4xl md:text-5xl font-bold text-[#1a202c] mb-6">
            Khi nào doanh nghiệp cần dịch vụ này?
          </h2>
          <p className="text-lg text-[#4a5568] max-w-3xl mx-auto">
          Bảo mật không phải là hoạt động một lần, mà là quy trình cần được đánh giá định kỳ.
          Dưới đây là những thời điểm quan trọng doanh nghiệp nên thực hiện Security Assessment.
          </p>
        </div>

        {/* Scenarios Grid */}
        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block w-16 h-16 border-4 border-[#06b6d4] border-t-transparent rounded-full animate-spin"></div>
            <p className="mt-4 text-gray-600">Đang tải...</p>
          </div>
        ) : scenarios.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-600">Chưa có nội dung</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-3 gap-8">
            {scenarios.map((scenario, index) => (
              <div 
                key={scenario.id}
                className={`group bg-white rounded-2xl p-8 border-2 border-[#e2e8f0] hover:border-[#06b6d4] hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 ${
                  isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                }`}
                style={{ 
                  transitionDelay: isVisible ? `${index * 0.1}s` : '0s'
                }}
              >
                {/* Icon */}
                <div className={`w-16 h-16 rounded-xl ${scenario.iconBg || 'bg-gradient-to-br from-[#06b6d4]/10 to-[#0891b2]/10'} flex items-center justify-center mb-6 group-hover:scale-110 group-hover:rotate-3 transition-all duration-300`}>
                  <i className={`${scenario.icon} text-3xl ${scenario.color || 'text-[#06b6d4]'}`}></i>
                </div>

                {/* Content */}
                <h3 className="font-['Inter',sans-serif] text-xl font-bold text-[#1a202c] mb-4 group-hover:text-[#06b6d4] transition-colors duration-300">
                  {scenario.title}
                </h3>
                <p className="text-[#4a5568] leading-relaxed">
                  {scenario.description}
                </p>

                {/* Hover indicator */}
                <div className="mt-6 flex items-center gap-2 text-[#2a4b7c] opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <span className="text-sm font-semibold">Tìm hiểu thêm</span>
                  <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform"></i>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}